// src/lib/calculator/half-life.ts
import type { Locale } from "@/config/locale";
import type { HalfLifeInput, HalfLifeResult, HalfLifeDataPoint } from "./types";

const LN2 = Math.LN2;

/** Elimination rate constant k = ln 2 / half-life. */
function rateConstant(halfLifeHours: number): number {
	if (halfLifeHours <= 0) {
		throw new Error(`halfLifeHours must be > 0, got ${halfLifeHours}`);
	}
	return LN2 / halfLifeHours;
}

/** Single-dose decay: concentration as % of the initial peak. */
export function decayPercent(hoursSinceDose: number, halfLifeHours: number): number {
	const k = rateConstant(halfLifeHours);
	if (hoursSinceDose < 0) return 0;
	return 100 * Math.exp(-k * hoursSinceDose);
}

/**
 * Multi-dose superposition: sum of contributions from each dose i administered
 * at time i * intervalHours, for i in [0, numDoses).
 */
export function multiDosePercent(
	hour: number,
	halfLifeHours: number,
	intervalHours: number,
	numDoses: number,
): number {
	if (numDoses > 1 && intervalHours <= 0) {
		throw new Error(`intervalHours must be > 0 when numDoses > 1, got ${intervalHours}`);
	}
	if (numDoses < 1) throw new Error(`numDoses must be >= 1, got ${numDoses}`);
	const k = rateConstant(halfLifeHours);
	let total = 0;
	for (let i = 0; i < numDoses; i++) {
		const t = hour - i * intervalHours;
		if (t < 0) break; // future dose — no contribution
		total += 100 * Math.exp(-k * t);
	}
	return total;
}

/** Accumulation factor: Cmax_ss / Cmax_single = 1 / (1 - e^(-k τ)). */
export function accumulationFactor(halfLifeHours: number, intervalHours: number): number {
	if (intervalHours <= 0) {
		throw new Error(`intervalHours must be > 0, got ${intervalHours}`);
	}
	const k = rateConstant(halfLifeHours);
	return 1 / (1 - Math.exp(-k * intervalHours));
}

/** Time to reach ~95 % of steady state: 4.32 half-lives. */
export function timeToSteadyStateHours(halfLifeHours: number): number {
	if (halfLifeHours <= 0) throw new Error(`halfLifeHours must be > 0, got ${halfLifeHours}`);
	return 4.32 * halfLifeHours;
}

/** Time until < 1 % of the last dose remains: 7 half-lives. */
export function eliminationAfterLastDoseHours(halfLifeHours: number): number {
	if (halfLifeHours <= 0) throw new Error(`halfLifeHours must be > 0, got ${halfLifeHours}`);
	return 7 * halfLifeHours;
}

/**
 * Human-readable duration label, no dependency on next-intl.
 * Choices: < 1h → minutes, < 24h → hours, else days with one decimal.
 */
export function formatDurationLabel(hours: number, locale: Locale): string {
	const roundHalfAway = (n: number, digits: number) => {
		const f = Math.pow(10, digits);
		return Math.round(n * f) / f;
	};
	if (hours < 1) {
		const minutes = Math.round(hours * 60);
		return locale === "de" ? `~${minutes} Min.` : `~${minutes} min`;
	}
	if (hours < 24) {
		return locale === "de" ? `~${Math.round(hours)} Std.` : `~${Math.round(hours)} h`;
	}
	const days = roundHalfAway(hours / 24, 1);
	if (locale === "de") {
		// Use comma as decimal separator in DE.
		const daysStr = Number.isInteger(days) ? String(days) : days.toString().replace(".", ",");
		return `~${daysStr} Tage`;
	}
	if (locale === "fr") {
		const daysStr = Number.isInteger(days) ? String(days) : days.toString().replace(".", ",");
		return `~${daysStr} jours`;
	}
	return `~${days} days`;
}

/**
 * Produce the full data series + summary metrics for the Half-Life Calculator UI.
 *
 * Chart window: max(5 × halfLife, 7 days, (numDoses-1) × interval + 7 × halfLife) — always
 * includes one full clearance cycle after the last dose so the "gone after" KPI is visible.
 *
 * Sample resolution: adapt to half-life so very short and very long HLs both render smoothly.
 */
export function computeHalfLifeResult(input: HalfLifeInput, locale: Locale): HalfLifeResult {
	const { halfLifeHours, intervalHours, numDoses } = input;
	if (halfLifeHours <= 0) throw new Error(`halfLifeHours must be > 0`);
	if (numDoses < 1) throw new Error(`numDoses must be >= 1`);
	if (numDoses > 1 && intervalHours <= 0) throw new Error(`intervalHours must be > 0 when numDoses > 1`);

	const accum = numDoses > 1 ? accumulationFactor(halfLifeHours, intervalHours) : 1;
	const cmaxSS = 100 * accum;
	const cminSS = cmaxSS * Math.exp(-rateConstant(halfLifeHours) * intervalHours);
	const tSS = timeToSteadyStateHours(halfLifeHours);
	const elim = eliminationAfterLastDoseHours(halfLifeHours);

	const lastDoseTime = (numDoses - 1) * intervalHours;
	const windowEnd = Math.max(5 * halfLifeHours, 168, lastDoseTime + elim);

	// ~240 points per chart is smooth without being expensive.
	const targetPoints = 240;
	const step = windowEnd / targetPoints;

	const dataPoints: HalfLifeDataPoint[] = [];
	const dosePeakTimes = new Set<number>();
	for (let i = 0; i < numDoses; i++) dosePeakTimes.add(i * intervalHours);

	// Build hour grid + inject dose-peak times so peaks aren't aliased.
	const hours = new Set<number>();
	for (let h = 0; h <= windowEnd; h += step) hours.add(Math.round(h * 1000) / 1000);
	dosePeakTimes.forEach((t) => hours.add(t));

	const sortedHours = Array.from(hours).sort((a, b) => a - b);
	for (const h of sortedHours) {
		const pct = multiDosePercent(h, halfLifeHours, intervalHours, numDoses);
		dataPoints.push({ hour: h, percent: pct });
	}

	return {
		dataPoints,
		cmaxFirst: 100,
		cmaxSteadyState: cmaxSS,
		cminSteadyState: cminSS,
		accumulationFactor: accum,
		timeToSteadyStateHours: tSS,
		timeToSteadyStateLabel: formatDurationLabel(tSS, locale),
		eliminationAfterLastDoseHours: elim,
	};
}
